import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import { selectUserLeadSource, selectIsLoggedIn, selectFilterUserRoles, selectAuthToken } from "store/user/account/selectors";

import Auth from "utils/auth0";

import NavLink from "components/NavLink";

import FileInvoiceDollarSolid from "assets/images/svg/file-invoice-dollar-solid.svg";
import ArrowRight from "assets/images/svg/arrow-right.svg";
import BrokerUser from "assets/images/svg/broker-user.svg";
import Store from "assets/images/svg/store.svg";
import CheckList from "assets/images/svg/clipboard.svg";
import Speedometer from "assets/images/svg/speedometer.svg";
import Analyze from "assets/images/svg/analyze.svg";
import PayoutWallet from "assets/images/svg/payout-wallet.svg";
import Transactions from "assets/images/svg/transactions.svg";

import { hasTenantWithPartnersTab, SOCIAL_LOGIN_LEAD_SOURCE, SUB_PAGES } from "constants/index";
import { useFormattedMessage } from "hooks";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
    products,
    users,
    investments,
    testingSuite,
    orders,
    payments,
    broker,
    performance,
    blockchain,
    miscellaneous,
    tenants,
    issuers,
    setting,
    productCatalog,
    documents,
    secondaryMarket,
    payouts,
    overview,
    updateText,
    platformSettings,
    account,
    investors,
    partners,
    investmentProducts,
    transactions,
    accountSupport,
    marketingMaterials,
    dashboard,
    personalData,
    provision,
    information,

    // ewallet,
} from "i18n/messages/adminMenu";

import { capitalizeFirstLetter } from "utils";
import { profile, referral, notification, tax, logoutTitle, changePasswordTitle } from "i18n/messages/accountMenu";
import SelectInput from "./SelectInput";
import { fetchManagerIssuersListStart } from "store/issuer-manager/actions";
import {
    selectIssuerManagerIssuersList,
    selectIssuerManagerLoading,
    selectIssuerManagerSelectedIssuer,
} from "store/issuer-manager/selectors";
import { useRouter } from "next/router";
import { inviteCustomers } from "i18n/messages/buttonTitles";
import { selectCurrentTenant } from "store/app/selectors";

function DashboardSideNavigation({
    className,
    mobile,
    menu: tabMenu,
    isLoggedIn,
    userRoles,
    userLeadSource,
    fetchManagerIssuersListStart,
    issuerManagerLoading,
    issuersList,
    currentTenant,
}) {
    const router = useRouter();
    const { formatMessage } = useFormattedMessage();
    const [selectedIssuer, setSelectedIssuer] = useState(null);

    const menu = tabMenu?.toLowerCase?.();
    const subPageConstants = SUB_PAGES;

    const menuData = {
        account: {
            [subPageConstants.PROFILE]: {
                faIcon: "fas fa-user",
                linkText: formatMessage(profile),
                linkUrl: "/account/profile",
            },
            [subPageConstants.INVITE_FRIENDS]: {
                faIcon: "fas fa-user-plus",
                linkText: formatMessage(referral),
                linkUrl: userRoles.broker ? "/broker/dashboard?goto=inviteCustomer" : "/account/referral",
            },
            [subPageConstants.EMAIL_SUBS]: {
                faIcon: "fas fa-envelope",
                linkText: formatMessage(notification),
                linkUrl: "/account/notification",
            },
            [subPageConstants.TAX]: {
                linkText: formatMessage(tax),
                svg: <FileInvoiceDollarSolid />,
                linkUrl: "/account/tax",
            },
            [subPageConstants.CHANGE_PASS]: {
                faIcon: "fas fa-unlock-alt",
                linkText: formatMessage(changePasswordTitle),
                linkUrl: "/account/change-password",
            },
            [subPageConstants.LOGOUT]: {
                linkClass: "desktop",
                faIcon: "fas fa-sign-out-alt",
                linkText: formatMessage(logoutTitle),
                linkUrl: "#",
                onClick: () => {
                    if (isLoggedIn) {
                        Auth.customLogout();
                    }
                },
                onKeyPress: () => {},
            },
        },
        admin: {
            [subPageConstants.PROJECTS]: {
                faIcon: "fas fa-compass",
                linkText: formatMessage(products),
                linkUrl: "/admin/products",
            },
            [subPageConstants.USERS]: {
                faIcon: "fas fa-users",
                linkText: formatMessage(users),
                linkUrl: "/admin/users",
            },
            [subPageConstants.INVESTMENTS]: {
                faIcon: "fas fa-bullhorn",
                linkText: formatMessage(investments),
                linkUrl: "/admin/investments",
            },
            [subPageConstants.PAYOUTS]: {
                svg: <PayoutWallet />,
                linkText: formatMessage(payouts),
                linkUrl: "/admin/payouts",
            },
            [subPageConstants.NOTIFICATION]: {
                faIcon: "far fa-bell",
                linkText: formatMessage(notification),
                linkUrl: "/admin/notification",
            },
            [subPageConstants.ISSUER]: {
                faIcon: "far fa-building",
                linkText: formatMessage(issuers),
                linkUrl: "/admin/issuers",
            },
            [subPageConstants.TENANT]: {
                faIcon: "fas fa-compass",
                linkText: formatMessage(tenants),
                linkUrl: "/admin/tenants",
            },
            [subPageConstants.TESTING_SUITE]: {
                svg: <Analyze />,
                linkText: formatMessage(testingSuite),
                linkUrl: "/admin/testing-suite",
            },
            [subPageConstants.ORDERS]: {
                svg: <CheckList />,
                linkText: formatMessage(orders),
                linkUrl: "/admin/orders",
            },
            [subPageConstants.PAYMENTS]: {
                faIcon: "fas fa-credit-card",
                linkText: formatMessage(payments),
                linkUrl: "/admin/payments",
            },
            [subPageConstants.SECONDARY_MARKET]: {
                svg: <Store />,
                linkText: formatMessage(secondaryMarket),
                linkUrl: "/admin/secondary-market",
            },
            [subPageConstants.BROKER]: {
                svg: <BrokerUser />,
                linkText: formatMessage(broker),
                linkUrl: "/admin/broker",
            },
            [subPageConstants.PERFORMANCE]: {
                svg: <Speedometer />,
                linkText: formatMessage(performance),
                linkUrl: "/admin/performance",
            },
            [subPageConstants.BLOCKCHAIN]: {
                faIcon: "fas fa-cubes",
                linkText: formatMessage(blockchain),
                linkUrl: "/admin/blockchain",
            },
            [subPageConstants.PLATFORM_SETTINGS]: {
                faIcon: "fas fa-cog",
                linkText: formatMessage(platformSettings),
                linkUrl: "/admin/platform-settings",
            },
            [subPageConstants.DOCUMENTS]: {
                faIcon: "fas fa-folder-open",
                linkText: formatMessage(documents),
                linkUrl: "/admin/documents",
            },
            [subPageConstants.MISCELLANEOUS]: {
                faIcon: "fas fa-retweet",
                linkText: formatMessage(miscellaneous),
                linkUrl: "/admin/miscellaneous",
            },
        },
        broker: {
            [subPageConstants.BROKER_DASHBOARD]: {
                faIcon: "fas fa-chart-line",
                linkText: formatMessage(dashboard),
                linkUrl: "/broker/dashboard",
            },
            [subPageConstants.PERSONAL_DATA]: {
                faIcon: "fas fa-user",
                linkText: formatMessage(personalData),
                linkUrl: "/broker/personal-data",
            },
            [subPageConstants.PROVISION]: {
                image: "/images/broker_provision.png",
                linkText: formatMessage(provision),
                linkUrl: "/broker/provision",
                imageProperty: {
                    height: 26,
                    width: 26,
                    marginRight: "10px",
                },
            },
            [subPageConstants.INFORMATION]: {
                image: "/images/broker_information.png",
                linkText: formatMessage(information),
                linkUrl: "/broker/information",
                imageProperty: {
                    height: 20,
                    width: 20,
                    marginRight: "16px",
                },
            },
            [subPageConstants.MARKETING_MATERIALS]: {
                faIcon: "fas fa-bullhorn",
                linkText: formatMessage(marketingMaterials),
                linkUrl: "/broker/marketing-materials",
            },
            [subPageConstants.INVITE_CUSTOMER]: {
                linkClass: "desktop-display",
                anchorClass: "btn-primary medium text-center mt-4 mw-100 d-block white-color",
                linkText: formatMessage(inviteCustomers),
                linkUrl: "#",
                onClick: () => jQuery(".broker-page .partner-heading button").click(),
            },
        },
        partner: {
            [subPageConstants.BROKER_OVERVIEW]: {
                faIcon: "fas fa-chart-pie",
                linkText: formatMessage(overview),
                linkUrl: "/partner/overview",
            },
            [subPageConstants.BROKER_PRODUCTS]: {
                faIcon: "fas fa-compass",
                linkText: formatMessage(investmentProducts),
                linkUrl: "/partner/products",
            },
            [subPageConstants.BROKER_TRANSACTIONS]: {
                svg: <Transactions />,
                linkText: formatMessage(transactions),
                linkUrl: "/partner/transactions",
            },
            [subPageConstants.MARKETING_MATERIALS]: {
                faIcon: "fas fa-bullhorn",
                linkText: formatMessage(marketingMaterials),
                linkUrl: "/partner/marketing-materials",
            },
            [subPageConstants.ACCOUNT_SUPPORT]: {
                faIcon: "fas fa-user",
                linkText: formatMessage(accountSupport),
                linkUrl: "/partner/account",
            },
        },
        tenant: {
            [subPageConstants.OVERVIEW]: {
                faIcon: "fas fa-chart-pie",
                linkText: formatMessage(overview),
                linkUrl: "/tenant/overview",
            },
            [subPageConstants.PRODUCT_CATALOGUE]: {
                faIcon: "fas fa-compass",
                linkText: formatMessage(productCatalog),
                linkUrl: "/tenant/product-catalog",
            },
            [subPageConstants.USERS]: {
                faIcon: "fas fa-users",
                linkText: formatMessage(investors),
                linkUrl: "/tenant/investors",
            },
            [subPageConstants.INVESTMENTS]: {
                faIcon: "fas fa-bullhorn",
                linkText: formatMessage(investments),
                linkUrl: "/tenant/investments",
            },
            [subPageConstants.PARTNERS]: {
                faIcon: "fas fa-user",
                linkText: formatMessage(partners),
                linkUrl: "/tenant/partners",
                forceActive: router.pathname === "/tenant/partners/[id]",
                isRender: hasTenantWithPartnersTab(currentTenant?.TechnicalName),
            },
            [subPageConstants.SETTING]: {
                faIcon: "fas fa-cog",
                linkText: formatMessage(setting),
                linkUrl: "/tenant/setting",
            },
        },
        issuers: {
            [subPageConstants.MANAGING_DIRECTOR]: {
                faIcon: "fas fa-users",
                linkText: formatMessage(overview),
                linkUrl: router.query.issuerUid
                    ? `/issuer-manager/overview?issuerUid=${router.query.issuerUid}`
                    : "/issuer-manager/overview",
            },
            [subPageConstants.OVERVIEW]: {
                faIcon: "fas fa-chart-pie",
                linkText: formatMessage(updateText),
                linkUrl: router.query.issuerUid
                    ? `/issuer-manager/update?issuerUid=${router.query.issuerUid}`
                    : "/issuer-manager/update",
            },
            [subPageConstants.PRODUCT]: {
                faIcon: "fas fa-compass",
                linkText: formatMessage(products),
                linkUrl: router.query.issuerUid
                    ? `/issuer-manager/products?issuerUid=${router.query.issuerUid}`
                    : "/issuer-manager/products",
            },
            [subPageConstants.USERS]: {
                faIcon: "fas fa-users",
                linkText: formatMessage(investors),
                linkUrl: router.query.issuerUid
                    ? `/issuer-manager/investors?issuerUid=${router.query.issuerUid}`
                    : "/issuer-manager/investors",
            },
            [subPageConstants.INVESTMENTS]: {
                faIcon: "fas fa-bullhorn",
                linkText: formatMessage(investments),
                linkUrl: router.query.issuerUid
                    ? `/issuer-manager/investments?issuerUid=${router.query.issuerUid}`
                    : "/issuer-manager/investments",
            },
            // [subPageConstants.EWALLET]: {
            //     faIcon: "fas fa-credit-card",
            //     linkText: formatMessage(ewallet),
            //     linkUrl: router.query.issuerUid
            //         ? `/issuer-manager/ewallet?issuerUid=${router.query.issuerUid}`
            //         : "/issuer-manager/ewallet",
            // },
            [subPageConstants.ACCOUNT]: {
                faIcon: "fas fa-user",
                linkText: formatMessage(account),
                linkUrl: router.query.issuerUid
                    ? `/issuer-manager/account?issuerUid=${router.query.issuerUid}`
                    : "/issuer-manager/account",
            },
        },
        umbrella: {
            [subPageConstants.USERS]: {
                faIcon: "fas fa-users",
                linkText: formatMessage(users),
                linkUrl: "/umbrella/users",
            },
            [subPageConstants.INVESTMENTS]: {
                faIcon: "fas fa-bullhorn",
                linkText: formatMessage(investments),
                linkUrl: "/umbrella/investments",
            },
            // [subPageConstants.SETTING]: {
            // 	faIcon: "fas fa-cog",
            // 	linkText: formatMessage(setting),
            // 	linkUrl: "/umbrella/settings",
            // }
        },
    };

    const sideMenu = menuData[menu] && Object.values(menuData[menu]);

    useEffect(() => {
        if (tabMenu === "issuers" && issuersList?.length == 0) {
            fetchManagerIssuersListStart();
        }
    }, []);

    useEffect(() => {
        if (selectedIssuer) {
            const queryString = router?.query?.id ? `&id=${router?.query?.id}` : "";
            router.push(`${router.pathname}?issuerUid=${selectedIssuer.value}${queryString}`, undefined, { shallow: true });
        }
    }, [selectedIssuer]);

    useEffect(() => {
        if (tabMenu === "issuers" && issuersList?.length > 0) {
            if (router.query.issuerUid) {
                const issuer = issuersList?.find((item) => item.value === router.query.issuerUid);
                if (issuer) {
                    setSelectedIssuer(issuer);
                } else {
                    setSelectedIssuer(issuersList?.[0]);
                }
            } else {
                setSelectedIssuer(issuersList?.[0]);
            }
        }
    }, [issuersList?.length, tabMenu]);

    const renderMenuData =
        Array.isArray(sideMenu) && sideMenu.length > 0
            ? sideMenu
                  .filter(Boolean)
                  .map(
                      (
                          {
                              isExternal = false,
                              linkClass,
                              anchorClass = "",
                              faIcon,
                              svg = "",
                              image = "",
                              imageProperty,
                              linkText,
                              linkUrl,
                              onClick,
                              onKeyPress,
                              isRender = true,
                              forceActive,
                          },
                          menuIndex,
                      ) => {
                          if (isRender) {
                              return (
                                  <Fragment key={"menuIndex=" + menuIndex}>
                                      {SOCIAL_LOGIN_LEAD_SOURCE.includes(userLeadSource) ? (
                                          linkUrl !== "/account/change-password" ? (
                                              <li className={linkClass}>
                                                  <NavLink
                                                      className={anchorClass}
                                                      isExternal={isExternal}
                                                      href={linkUrl}
                                                      activeClassName="active"
                                                      forceActive={forceActive}
                                                      onClick={onClick}
                                                      onKeyPress={onKeyPress}
                                                  >
                                                      {faIcon && <i className={faIcon} />}
                                                      {image && (
                                                          <img
                                                              src={image}
                                                              height={imageProperty.height}
                                                              width={imageProperty.width}
                                                              style={{
                                                                  marginRight: imageProperty.marginRight,
                                                                  filter: "grayscale(100%)",
                                                              }}
                                                              alt={linkText}
                                                          />
                                                      )}
                                                      {svg}
                                                      {linkText}
                                                  </NavLink>
                                              </li>
                                          ) : null
                                      ) : (
                                          <li key={menuIndex} className={linkClass}>
                                              <NavLink
                                                  className={anchorClass}
                                                  isExternal={isExternal}
                                                  href={linkUrl}
                                                  activeClassName="active"
                                                  onClick={onClick}
                                                  forceActive={forceActive}
                                                  onKeyPress={onKeyPress}
                                              >
                                                  {faIcon && <i className={faIcon} />}
                                                  {image && (
                                                      <img
                                                          src={image}
                                                          height={imageProperty.height}
                                                          width={imageProperty.width}
                                                          style={{
                                                              marginRight: imageProperty.marginRight,
                                                              filter: "grayscale(100%)",
                                                          }}
                                                          alt={linkText}
                                                      />
                                                  )}
                                                  {svg}
                                                  {linkText}
                                              </NavLink>
                                          </li>
                                      )}
                                  </Fragment>
                              );
                          }
                      },
                  )
            : null;

    const onIssuerSelect = (value) => {
        setSelectedIssuer(value);
    };

    return (
        <>
            {mobile ? (
                <div className={`side-menu mobile ${className}`}>
                    <div
                        className="flex justify-between align-center head "
                        onClick={(e) => {
                            jQuery(e.target).next("ul").slideToggle();
                            jQuery(e.target).closest("div").toggleClass("active");
                        }}
                    >
                        <h4 className="mt-0">
                            {tabMenu === "issuers"
                                ? issuersList?.length === 1
                                    ? selectedIssuer?.label
                                    : "Issuer"
                                : capitalizeFirstLetter(tabMenu)}
                        </h4>
                        <ArrowRight />
                    </div>

                    <ul style={{ display: "none" }}>
                        {tabMenu === "issuers" ? (
                            issuerManagerLoading ? (
                                <Skeleton style={{ width: "100%", height: "40px", borderRadius: "8px !important" }} />
                            ) : issuersList?.length > 1 ? (
                                <div className="pt-4 px-4">
                                    <SelectInput
                                        name="issuer"
                                        className="filter-select"
                                        options={issuersList}
                                        placeholder="Issuer name"
                                        value={selectedIssuer}
                                        hideSelectedOptions={false}
                                        onChange={(value) => onIssuerSelect(value)}
                                    />
                                </div>
                            ) : null
                        ) : null}
                        {renderMenuData}
                    </ul>
                </div>
            ) : (
                <div className={`side-menu desktop ${className}`}>
                    {tabMenu === "issuers" ? (
                        issuerManagerLoading ? (
                            <Skeleton style={{ width: "100%", height: "40px", borderRadius: "8px !important" }} />
                        ) : issuersList?.length > 1 ? (
                            <SelectInput
                                name="issuer"
                                className="filter-select"
                                options={issuersList}
                                placeholder="Issuer name"
                                value={selectedIssuer}
                                hideSelectedOptions={false}
                                onChange={(value) => onIssuerSelect(value)}
                            />
                        ) : (
                            <h4 className="mt-0">{selectedIssuer?.label ?? "Issuer"}</h4>
                        )
                    ) : (
                        <h4 className="mt-0">
                            {capitalizeFirstLetter(
                                tabMenu == "tenant"
                                    ? formatMessage("nav_tenant")
                                    : tabMenu === "umbrella"
                                      ? "Liability umbrella"
                                      : tabMenu,
                            )}
                        </h4>
                    )}
                    <ul>{renderMenuData}</ul>
                </div>
            )}
        </>
    );
}

const mapStateToProps = createStructuredSelector({
    isLoggedIn: selectIsLoggedIn,
    userLeadSource: selectUserLeadSource,
    userRoles: selectFilterUserRoles,
    token: selectAuthToken,
    selectedIssuerUid: selectIssuerManagerSelectedIssuer,
    issuerManagerLoading: selectIssuerManagerLoading,
    issuersList: selectIssuerManagerIssuersList,
    currentTenant: selectCurrentTenant,
});
const mapDispatchToProps = (dispatch) => ({
    fetchManagerIssuersListStart: () => dispatch(fetchManagerIssuersListStart()),
});
export default connect(mapStateToProps, mapDispatchToProps)(DashboardSideNavigation);
